import { property, RayHit } from '@wonderlandengine/api/decorators.js';
import { Component, Object3D, PhysXComponent } from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';
import { VEC3_DOWN, VEC3_Y } from '../utils/constants.js';
import { GlobalEvents } from '../core/global-events.js';

/**
 * Direction type for Keyboard Inputs.
 */
type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * KeyMap for Keyboard Inputs.
 * If keys need to change or be added in the future, change here.
 *
 * @remarks
 * Because KeyboardEvent.code is used there is no need to worry about keyboard layout.
 * For example: KeyW is always Up, no matter if the keyboard is QWERTY or AZERTY.
 */
const keyMap: { [key: string]: Direction } = {
    ArrowUp: 'up',
    KeyW: 'up',
    ArrowRight: 'right',
    KeyD: 'right',
    ArrowDown: 'down',
    KeyS: 'down',
    ArrowLeft: 'left',
    KeyA: 'left',
};

/**
 * FPS type movement with WASD keys based on Physx.
 *
 * @remarks
 * The component is dependent on an {@link PhysXComponent} component on the same object.
 *
 * Physx Component Settings:
 * - Shape: Sphere
 * - linearDamping: 5
 * - angularDamping: 5
 * - lock all angularAxis except for Y (For rotation torque ability)(Can be disabled if you don't want Rotation)
 */
export class WasdControlsFps extends Component {
    static TypeName = 'wasd-controls-fps';

    /** Whether or not the player can move */
    @property.bool(true)
    moveActive = true;

    /** Whether or not the player can rotate */
    @property.bool(true)
    rotActive = true;

    /** Movement speed of the player */
    @property.float(12)
    moveSpeed = 12;

    /** nonVRCamera Object to get the forward direction from for movement */
    @property.object({ required: true })
    nonVRCamera!: Object3D;

    @property.float(0.4)
    playerHeight!: number;
    @property.float(0.5)
    playerRadius!: number;

    private tmpPos = vec3.create();
    private tmpPosOld = vec3.create();
    private tmpVec = vec3.create();
    private tmpVec2 = vec3.create();
    private fwd = vec3.create();
    private lastSafePos: vec3 | undefined = undefined;
    private currentState!: string;
    private rayhit: RayHit | undefined = undefined;
    private hittingObject: Object3D | null = null;
    private teleportFlag = false;

    private up: boolean = false;
    private right: boolean = false;
    private down: boolean = false;
    private left: boolean = false;
    private headForward: vec3 = [0, 0, 0];
    private physxComponent!: PhysXComponent;

    init() {
        this.up = false;
        this.right = false;
        this.down = false;
        this.left = false;
    }

    start() {
        const tempPhysx = this.object.getComponent(PhysXComponent);
        if (!tempPhysx) {
            console.error(
                'WasdControlsFps: No PhysX Component found on object. Please add a PhysX Component to the object.'
            );
        }
        this.physxComponent = tempPhysx!;
    }

    onActivate(): void {
        // Set Keyboard Inputs Listeners
        window.addEventListener('keydown', this.pressKeyboard.bind(this));
        window.addEventListener('keyup', this.releaseKeyboard.bind(this));
        GlobalEvents.instance.teleportRequested.add(
            this._onTeleportRequested,
            this
        );
    }

    onDeactivate(): void {
        // Remove Keyboard Inputs Listeners
        window.removeEventListener('keydown', this.pressKeyboard.bind(this));
        window.removeEventListener('keyup', this.releaseKeyboard.bind(this));
        GlobalEvents.instance.teleportRequested.remove(
            this._onTeleportRequested
        );
    }

    update(dt: number) {
        // do not move if no input
        // if (!this.up && !this.down && !this.left && !this.right) {
        //     return;
        // }

        // Move via Force Physx
        this.headForward = [0, 0, 0];
        let direction: vec3 = [0, 0, 0];
        if (this.up) direction[2] -= 1.0;
        if (this.down) direction[2] += 1.0;
        if (this.left) direction[0] -= 1.0;
        if (this.right) direction[0] += 1.0;

        this.nonVRCamera.getForwardWorld(this.fwd);
        direction[1] = 0; // Do not move on Y axis
        // If no direction is set, do not move
        // Move according to headObject Forward Direction
        vec3.normalize(direction, direction);
        direction[0] *= this.moveSpeed;
        direction[2] *= this.moveSpeed;
        this.nonVRCamera.getForwardWorld(this.headForward);

        this.nonVRCamera.getPositionWorld(this.tmpPos);
        // Combine direction with headObject
        vec3.transformQuat(
            direction,
            direction,
            this.nonVRCamera.getTransformWorld()
        );

        let speed = this.moveSpeed;
        // Scale the fwd vector by the capsule radius
        const capsuleRadius = this.playerRadius;
        const rayStart = vec3.create();

        vec3.scaleAndAdd(rayStart, this.tmpPos, direction, -capsuleRadius);

        vec3.copy(this.tmpPosOld, this.tmpPos);
        const dist = dt * speed;

        // Move the tmpPos along the fwd direction
        vec3.scaleAndAdd(this.tmpPos, this.tmpPos, direction, dist);

        // check for objstacles 40cm above the ground
        rayStart[1] += 0.4;

        /* Check for obstacles */
        this.rayhit = this.engine.physics?.rayCast(
            rayStart,
            this.fwd,
            0b010 /* Blocked */,
            dist * 8
        );
        let ignoreCollision = false;
        const distanceToNavmesh = this.checkNavmesh(this.tmpPos);

        if (distanceToNavmesh !== undefined) {
            // If we are on the navmesh, we can move
            this.lastSafePos = vec3.clone(this.tmpPos);

            // Calculate final position:
            // tmpPos.y + (distance - 0.5) + desiredHeight
            // This adjusts for the 0.5 offset in the ray and sets the desired height
            //this.tmpPos[1] += distanceToNavmesh - 0.5 + 0.5;
            this.tmpPos[1] =
                distanceToNavmesh.locations[0][1] + this.playerHeight;
            this.object.setPositionWorld(this.tmpPos);
        } else if (this.lastSafePos) {
            // No navmesh found - move to last safe position
            this.object.setPositionWorld(this.lastSafePos);
        } else {
            // No safe position either - use default height
            //  playerPosition[1] = -0.5;
            //this.object.setPositionWorld(playerPosition);
        }
        // if (this.rayhit!.hitCount > 0 || distanceToNavmesh === undefined) {
        //     // We hit something or we're not on the navmesh, so don't move

        //     if (this.rayhit!.distances[0] === 0) {
        //         // ray starts inside the object, better to ignore it
        //         ignoreCollision = true;
        //     }

        //     if (this.rayhit!.normals![0]) {
        //         // check if we are faceing the object
        //         if (vec3.dot(this.tmpVec!, this.rayhit!.normals[0]) < 0) {
        //             return;
        //         }
        //     }
        // }
        // if (
        //      ignoreCollision ||
        //     this.rayhit!.hitCount > 0 &&
        //     this.rayhit!.objects[0]!.name === 'NavMesh'
        // ) {
        //     this.tmpPos[1] -= this.rayhit!.distances[0] || 0;
        //     this.object.setPositionWorld(this.tmpPos);
        //     this.object.translateObject([0, this.playerHeight, 0]);
        // }

        // if (this.moveActive) {
        //     if (this.up || this.down || this.left || this.right) {
        //         // Do not move on Y axis
        //         direction[1] = 0;
        //         // Add force to Physx Component to move the player
        //         this.physxComponent.addForce(direction);
        //     }
        // }
    }

    /**
     * Check if the target position is on the navmesh
     */
    checkNavmesh(targetPosition: vec3): RayHit | undefined {
        vec3.set(
            this.tmpVec2,
            targetPosition[0],
            targetPosition[1] + 0.5,
            targetPosition[2]
        );
        const rayhit = this.engine.physics?.rayCast(
            this.tmpVec2,
            VEC3_DOWN,
            255,
            1
        )!;

        if (rayhit.hitCount > 0) {
            if (rayhit.objects[0]?.name === 'NavMesh') {
                //this.lastSafePos = vec3.clone(targetPosition);
                return rayhit;
            }
        }
        return undefined;
    }

    pressKeyboard(input: KeyboardEvent) {
        const direction = keyMap[input.code];

        if (direction) {
            this[direction] = true;
        }
    }

    releaseKeyboard(input: KeyboardEvent) {
        const direction = keyMap[input.code];

        if (direction) {
            this[direction] = false;
        }
    }

    private _onTeleportRequested = (position: vec3) => {
        this.object!.setPositionWorld(position);
    };
}
